/**
 * Utility functions for working with Promises and async operations
 */

/**
 * Creates a promise that resolves after the specified number of milliseconds
 * @param ms - The number of milliseconds to delay
 * @returns A promise that resolves after the specified delay
 *
 * Example:
 * ```
 * const result = await delay(1000)
 * console.log(result) // "Hello, world!"
 * ```
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

/**
 * Creates a cancellable promise that resolves after the specified number of milliseconds
 * @param ms - The number of milliseconds to delay
 * @param signal - The AbortSignal to listen to for cancellation
 * @returns A promise that resolves after the specified delay or rejects if aborted
 *
 * Example:
 * ```
 * const controller = new AbortController()
 * try {
 *   await cancellableDelay(2000, controller.signal)
 *   console.log('Delay completed')
 * } catch (error) {
 *   if (error.message === 'Operation aborted') {
 *     console.log('Delay was cancelled')
 *   }
 * }
 *
 * // To cancel the delay:
 * controller.abort()
 * ```
 */
export function cancellableDelay(
  ms: number,
  signal: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error('Operation aborted'))
      return
    }

    const timeoutId = window.setTimeout(() => {
      if (signal.aborted) {
        reject(new Error('Operation aborted'))
      } else {
        resolve()
      }
    }, ms)

    signal.addEventListener('abort', () => {
      window.clearTimeout(timeoutId)
      reject(new Error('Operation aborted'))
    })
  })
}

/**
 * Delays the execution of an action by a specified number of milliseconds
 * @param ms - The number of milliseconds to delay
 * @param action - The action to execute after the delay
 * @returns A promise that resolves when the action is complete
 *
 * Example:
 * ```
 * await after(1000, () => console.log('Hello, world!'))
 * ```
 */
export async function after<T>(
  ms: number,
  action: () => Promise<T>
): Promise<T> {
  await delay(ms)
  return action()
}

/**
 * A class that provides control over a Promise's resolution
 * Similar to TaskCompletionSource in .NET or Deferred in other languages
 * 
 * Example:
 * ```
 * async function doWork(checkFn: () => boolean, timeoutMs = 3000): Promise<string> {
 *   const tac = new TimeoutAbortController(timeoutMs, new Error('Operation timed out'))
 *   const signal = tac.signal
 *
 *   try {
 *     while (checkFn()) {
 *       if (signal.aborted) {
 *         throw new Error(signal.reason?.message ?? 'Aborted')
 *       }
 *       await delay(200)
 *     }
 *
 *     return 'Work finished'
 *   } finally {
 *     tac.abort()
 *   }
}
 * ```
 */
export class PromiseController<T> {
  promise: Promise<T>
  resolve!: (value: T) => void
  reject!: (reason?: unknown) => void

  constructor() {
    this.promise = new Promise((res, rej) => {
      this.resolve = res
      this.reject = rej
    })
  }
}

/**
 * An AbortController that automatically aborts after a specified timeout
 *
 * Example:
 * ```
 * function delayedGreeting(name: string): Promise<string> {
 *   const pc = new PromiseController<string>()
 *
 *   setTimeout(() => {
 *     pc.resolve(`Hello, ${name}!`)
 *   }, 1000)
 *
 *   return pc.promise
 * }
 * ```
 */
export class TimeoutAbortController extends AbortController {
  private _reason: unknown
  private _timeoutId: ReturnType<typeof setTimeout>

  /**
   * Creates a new TimeoutAbortController
   * @param ms - The timeout duration in milliseconds
   * @param reason - The reason for the timeout
   */
  constructor(ms: number, reason: unknown) {
    super()
    this._reason = reason
    this._timeoutId = setTimeout(() => super.abort(this._reason), ms)
  }

  /**
   * Aborts the controller and clears the timeout
   * @param reason - Optional reason for the abort
   */
  override abort(reason?: unknown): void {
    clearTimeout(this._timeoutId)
    super.abort(reason ?? this._reason)
  }
}

// example  1
export async function fetchWithTimeout<T>(
  promise: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  timeoutMessage?: string
) {
  const controller = new AbortController()
  const { signal } = controller

  // Create a promise that will throw an error if the operation takes too long
  const timeoutPromise = after(timeoutMs, () => {
    controller.abort()
    throw new Error(timeoutMessage ?? 'Operation timed out')
  })

  // Fire off a GET request
  const fetchPromise = promise(signal)

  // Race them: whichever settles first
  return Promise.race([fetchPromise, timeoutPromise])
}

// example 2
export async function fetchWithTimeoutOrDefault<T>(
  promise: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  defaultValue: T
) {
  const controller = new AbortController()
  const { signal } = controller

  const timeoutPromiseWithDefaultValue = after(timeoutMs, async () => {
    controller.abort()
    return defaultValue
  })
  // Fire off the fetch
  const fetchPromise = promise(signal)

  // Race them: whichever settles first
  return Promise.race([fetchPromise, timeoutPromiseWithDefaultValue])
}

export async function waitFor(
  conditionFn: () => Promise<boolean>,
  interval = 100,
  timeout = 5000
) {
  const start = Date.now()
  while (true) {
    if (await conditionFn()) return
    if (Date.now() - start >= timeout) throw new Error('waitFor: timeout')
    await delay(interval)
  }
}
